/**
 * Constants for the ASIN Essentials Report.
 *
 * Unlike the PRM reports (which read their S3 location from a backend
 * ReportConfig record), this report has no backend config yet, so the S3
 * location is derived client-side from the deployment stage — mirroring the
 * pattern used by `useChangeHistoryExport.ts`.
 *
 * Full bucket name: `verity-reports-${stage}` (dev falls back to `-alpha`).
 * Object key points at the single ~420MB JSON dataset.
 */

import { AwsRegion } from 'src/types/common/api.types';
import { getStage } from 'src/utils/environmentUtils';

/** Report unique code — used for routing and metrics. */
export const ASIN_ESSENTIALS_REPORT_UNIQUE_CODE = 'ASIN_ESSENTIALS';

/** Human-readable report name for metrics / export file naming. */
export const ASIN_ESSENTIALS_REPORT_NAME = 'ASIN Essentials Report';

/** Static route path (bypasses the config-driven ReportRouteGuard). */
export const ASIN_ESSENTIALS_REPORT_ROUTE = '/reports/asin-essentials-report';

/** S3 bucket prefix. Full name: `verity-reports-${stage}`. */
export const REPORTS_BUCKET_PREFIX = 'verity-reports';

/**
 * S3 prefix for the ASIN essentials dataset. Data is partitioned by
 * product_code: `${prefix}/product_code=${code}/data.json`.
 */
export const ASIN_ESSENTIALS_S3_PREFIX = 'reports/asin_program_attributes_essentials_report';

/** The reports bucket lives in us-east-1. */
export const ASIN_ESSENTIALS_REGION: AwsRegion = 'us-east-1';

/** Resolve the stage-specific bucket name. Local dev shares the alpha bucket. */
export const getReportsBucket = (): string => {
  const stage = getStage();
  if (stage === 'dev') return `${REPORTS_BUCKET_PREFIX}-alpha`;
  return `${REPORTS_BUCKET_PREFIX}-${stage}`;
};

/** Full s3:// URI for a single product_code partition's data.json. */
export const getAsinEssentialsPartitionURI = (productCode: string): string =>
  `s3://${getReportsBucket()}/${ASIN_ESSENTIALS_S3_PREFIX}/product_code=${productCode}/data.json`;

/** Full s3:// URI for the report-level metadata.json (Last Updated timestamp). */
export const getAsinEssentialsMetadataURI = (): string =>
  `s3://${getReportsBucket()}/${ASIN_ESSENTIALS_S3_PREFIX}/metadata.json`;

/** Sentinel option value representing "All Countries" in the country filter. */
export const ALL_COUNTRIES_VALUE = '__ALL__';

/** Sentinel option value representing "All Products" in the product filter. */
export const ALL_PRODUCTS_VALUE = '__ALL_PRODUCTS__';
